import React, { useEffect, useState } from 'react';
import { usePreloadStore } from '../../store/preloadStore';
import { authService } from '../../features/auth/services/authService';

interface PreloadProviderProps {
  children: React.ReactNode;
}

interface LoadingSpinnerProps {
  message?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ message = 'Loading application data...' }) => (
  <div className="fixed inset-0 bg-theme-bg dark:bg-dark-theme-bg flex items-center justify-center z-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-accent dark:border-dark-theme-accent mx-auto mb-4"></div>
      <p className="text-theme-text-primary dark:text-dark-theme-text-primary text-lg">{message}</p>
      <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary text-sm mt-2">Preparing your workspace...</p>
    </div>
  </div>
);

const ErrorDisplay: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="fixed inset-0 bg-theme-bg dark:bg-dark-theme-bg flex items-center justify-center z-50">
    <div className="text-center max-w-md mx-auto p-6">
      <div className="text-red-500 text-6xl mb-4">⚠️</div>
      <h2 className="text-xl font-semibold text-theme-text-primary dark:text-dark-theme-text-primary mb-2">Failed to Load Application Data</h2>
      <p className="text-theme-text-secondary dark:text-dark-theme-text-secondary mb-4">{error}</p>
      <div className="space-y-2">
        <button
          onClick={onRetry}
          className="w-full bg-theme-accent dark:bg-dark-theme-accent text-white px-4 py-2 rounded hover:opacity-90 transition-opacity"
        >
          Retry Loading
        </button>
        <button
          onClick={() => window.location.reload()}
          className="w-full bg-theme-border dark:bg-dark-theme-border text-theme-text-primary dark:text-dark-theme-text-primary px-4 py-2 rounded hover:opacity-90 transition-opacity"
        >
          Reload Page
        </button>
      </div>
    </div>
  </div>
);

export const PreloadProvider: React.FC<PreloadProviderProps> = ({ children }) => {
  const { initializePreload, isLoading, isInitialized, error } = usePreloadStore();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuthAndInitialize = async () => {
      try {
        // Check if user is authenticated
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);

        // Only preload if authenticated
        if (authenticated) {
          await initializePreload();
        }
      } catch (error) {
        console.error('Preload initialization failed:', error);
        setIsAuthenticated(false);
      }
    };

    checkAuthAndInitialize();
  }, [initializePreload]);

  const handleRetry = async () => {
    try {
      await initializePreload();
    } catch (error) {
      console.error('Retry failed:', error);
    }
  };

  // Show loading spinner only during preload (after user is authenticated)
  if (isAuthenticated && isLoading && !isInitialized) {
    return <LoadingSpinner />;
  }

  // Show error if preload failed
  if (error && isAuthenticated) {
    return <ErrorDisplay error={error} onRetry={handleRetry} />;
  }

  // If not authenticated, let the app handle auth flow
  // If authenticated and initialized, render children
  return <>{children}</>;
};

export default PreloadProvider;